import { ReactElement, useEffect } from 'react';

import 'chime-ui/normalize.css';
import 'chime-ui/custom.css';
import 'custom-event-polyfill';
import 'proxy-polyfill';

import { DeviceIntelligenceProvider } from '@1debit/device-intelligence';
import { datadogRum } from '@datadog/browser-rum';
import { LazyMotion } from 'framer-motion';
import { AppProps } from 'next/app';
import getConfig from 'next/config';
import smoothscroll from 'smoothscroll-polyfill';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

import { colors, fonts, typography } from 'chime-ui/system';
import media from 'chime-ui/system/media';

import { UserContextProvider } from 'hooks/useUserContext';

import { deviceIntelligenceEnvironment } from 'utils/device-intelligence/deviceIntelligenceUtils';
import { initExPlat } from 'utils/ExPlat/explat';
import { ApolloProviderWrapper } from 'utils/graphql/client';

if (process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
  require('../mocks');
}

const {
  publicRuntimeConfig: { deployEnvironment },
} = getConfig();

const isProductionBuild = process.env.NODE_ENV === 'production';
const isDevelopmentBuild = process.env.NODE_ENV === 'development';
const assetBaseUrl = isDevelopmentBuild ? '' : '/pay';

if (isProductionBuild) {
  datadogRum.init({
    applicationId: process.env.DATADOG_APPLICATION_ID,
    clientToken: process.env.DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'pay-anyone-client',
    env: deployEnvironment,
    version: process.env.CIRCLE_SHA1,
    sampleRate: 100,
    premiumSampleRate: 0,
    trackInteractions: true,
    allowedTracingOrigins: [`${process.env.CONSUMER_HOST}`],
    beforeSend: event => {
      event.view.url = event.view.url.replace(
        /((\/pay\/)|(\/request\/))[0-9a-zA-Z-]{23}/,
        '$1[REDACTED pay friends token]'
      );
    },
  });
}

const loadFramerMotionFeatures = () =>
  import('utils/framer-motion/features').then(res => res.default);

export default function PayAnyoneClient({
  Component,
  pageProps,
}: AppProps): ReactElement {
  initExPlat();

  const theme = {
    colors,
    fonts,
    typography,
  };

  useEffect(() => {
    // Polyfill for smooth scrolling in Safari & iOS
    // Placed here because it depends on window
    smoothscroll.polyfill();
  }, []);

  return (
    <DeviceIntelligenceProvider
      environment={deviceIntelligenceEnvironment(deployEnvironment)}
    >
      <ApolloProviderWrapper>
        <ThemeProvider theme={theme}>
          <LazyMotion features={loadFramerMotionFeatures} strict>
            <GlobalStyle />
            <UserContextProvider>
              <Component {...pageProps} />
            </UserContextProvider>
          </LazyMotion>
        </ThemeProvider>
      </ApolloProviderWrapper>
    </DeviceIntelligenceProvider>
  );
}

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Circular Pro';
    font-style: normal;
    font-weight: 500;
    font-display: fallback;
    src: url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-book.eot'
    }); /* IE9 Compat Modes */
    src: local('Circular Pro Book'), local('CircularPro-Book'),
      url(${
        assetBaseUrl + '/fonts/lineto-circular-pro-book.eot?#iefix'
      }) format('embedded-opentype'),
      /* IE6-IE8 */ url(${
        assetBaseUrl + '/fonts/lineto-circular-pro-book.woff2'
      }) format('woff2'),
      /* Super Modern Browsers */ url(${
        assetBaseUrl + '/fonts/lineto-circular-pro-book.woff'
      })
        format('woff'); /* Modern Browsers */
  }

  @font-face {
    font-family: 'Circular Pro';
    font-style: normal;
    font-weight: 700;
    font-display: fallback;
    src: url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.eot'
    }); /* IE9 Compat Modes */
    src: local('Circular Pro Bold'), local('CircularPro-Bold'),
    url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.eot?#iefix'
    }) format('embedded-opentype'),
    /* IE6-IE8 */ url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.woff2'
    }) format('woff2'),
    /* Super Modern Browsers */ url(${
      assetBaseUrl + '/fonts/lineto-circular-pro-bold.woff'
    })
      format('woff'); /* Modern Browsers */
  }

  body{
    font-family: ${fonts.primary};
    color: ${colors.notBlack};
    background-color: ${colors.white};
    min-height: 100%;
    height: auto;

    ${media.tablet`
      background-color: ${colors.alabaster};
    `};
  }`;
